import { Box, Button, CircularProgress, Dialog, DialogContent, Divider, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material'
import { DateRangePicker, MultiInputDateRangeField } from '@mui/x-date-pickers-pro'
import { addDays, addHours, addMonths, differenceInMonths } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'

import Api from '../../utils/api'
import useDates from '../../utils/hooks/useDates'
import useIsMobile from '../../utils/hooks/useIsMobile'

import useStyles from './pause-dialog.styles'

type DurationType = '1h' | '2h' | '4h' | 'restOfDay' | 'untilSunday' | 'range'

interface FormValues {
  duration: DurationType | ''
  dateRange: {
    startDate: Date | ''
    endDate: Date | ''
  }
}

const PauseDialog = ({
  device,
  selectedTimezone,
  openPauseDialog,
  setOpenPauseDialog,
  setDevice,
}: {
  device: IDeviceType | undefined
  selectedTimezone: string | undefined
  openPauseDialog: boolean
  setOpenPauseDialog: React.Dispatch<React.SetStateAction<boolean>>
  setDevice: React.Dispatch<React.SetStateAction<IDeviceType | undefined>>
}) => {
  const classes = useStyles
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useIsMobile()
  const { formatDate, timezoneAbbr, zonedEndOfDay, zonedEndOfWeek } = useDates(selectedTimezone)

  const initialValues: FormValues = { duration: '', dateRange: { startDate: '', endDate: '' } }

  const dateOfNextSunday = selectedTimezone ? formatDate(addDays(zonedEndOfWeek(new Date(), { weekStartsOn: 0 }), 1)) : ''

  const isRangeGreaterThan4Months = (startDate: string | Date, endDate: string | Date) => differenceInMonths(endDate ?? '', startDate ?? '') > 4

  const handleSubmit = async ({ duration, dateRange }: FormValues) => {
    if (!selectedTimezone || !device) return

    const now = toZonedTime(new Date(), selectedTimezone)
    let startTime = now
    let endTime = now
    if (duration === '1h') {
      endTime = addHours(now, 1)
    }
    if (duration === '2h') {
      endTime = addHours(now, 2)
    }
    if (duration === '4h') {
      endTime = addHours(now, 4)
    }
    if (duration === 'restOfDay') {
      endTime = zonedEndOfDay(now)
    }
    if (duration === 'untilSunday') {
      endTime = zonedEndOfDay(zonedEndOfWeek(now, { weekStartsOn: 1 })) // Assuming Sunday as the last day
    }
    if (duration === 'range') {
      startTime = toZonedTime(new Date(dateRange.startDate ?? ''), selectedTimezone!)
      endTime = zonedEndOfDay(toZonedTime(new Date(dateRange.endDate ?? ''), selectedTimezone!))
    }

    const endTimeISO = endTime.toISOString()
    const startTimeISO = startTime.toISOString()
    const res = await Api.post(`/api/v2/nodes/reactions/pause`, {
      deviceIds: [device.node.id],
      endTime: endTimeISO,
      startTime: startTimeISO,
    })
    if (res.ok) {
      setDevice((prev) => (prev ? { ...prev, node: { ...prev.node, pauseStartDate: startTimeISO, pauseEndDate: endTimeISO } } : prev)) // Refresh the device pause data
      setOpenPauseDialog(false)
    } else {
      enqueueSnackbar('There was a problem pausing the device')
    }
  }

  return (
    <Dialog open={openPauseDialog} fullScreen={isMobile} slots={{ backdrop: () => <Box sx={classes.backdrop} /> }}>
      <Box sx={classes.dialogHeader}>
        <Typography variant="body1">Pause this device</Typography>
      </Box>

      <Divider />
      <DialogContent>
        <Grid container={true} sx={classes.mainContainer}>
          <Typography variant="body2">While paused, this device will stop sending notifications to all recipients. </Typography>

          {!selectedTimezone || !timezoneAbbr ? (
            <Grid sx={classes.loadingContainer}>
              <CircularProgress />
            </Grid>
          ) : (
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ values, setFieldValue, isSubmitting }) => {
                return (
                  <Form style={classes.form}>
                    <Grid container={true} sx={classes.formContent}>
                      <Typography variant="subtitle2">Select Duration</Typography>
                      <RadioGroup name="duration" onChange={(event) => setFieldValue('duration', event.target.value)} sx={classes.radioGroup}>
                        <FormControlLabel value="1h" control={<Radio checked={values.duration === '1h'} />} label="1 hour" />
                        <FormControlLabel value="2h" control={<Radio checked={values.duration === '2h'} />} label="2 hours" />
                        <FormControlLabel value="4h" control={<Radio checked={values.duration === '4h'} />} label="4 hours" />
                        <FormControlLabel
                          value="restOfDay"
                          control={<Radio checked={values.duration === 'restOfDay'} />}
                          label={`Until the end of the day, 11:59 PM - ${timezoneAbbr}`}
                        />
                        <FormControlLabel
                          value="untilSunday"
                          control={<Radio checked={values.duration === 'untilSunday'} />}
                          label={`Until Sunday, ${dateOfNextSunday}, 11:59 PM - ${timezoneAbbr}`}
                        />
                        <Grid container={true}>
                          <Grid>
                            <FormControlLabel value="range" control={<Radio checked={values.duration === 'range'} />} label="From:" sx={classes.radioFrom} />
                          </Grid>
                          <Grid sx={classes.dateRangeContainer}>
                            <DateRangePicker
                              onOpen={() => setFieldValue('duration', 'range')}
                              onChange={(value) => {
                                setFieldValue('dateRange', { startDate: value[0], endDate: value[1] })
                                setFieldValue('duration', 'range')
                              }}
                              minDate={new Date()}
                              shouldDisableDate={(date, position) => {
                                if (position === 'start') return false
                                return values.dateRange.startDate ? date > addMonths(new Date(values.dateRange.startDate), 4) : false
                              }} // Limit to 4 months
                              localeText={{ start: 'Start date', end: 'End date', toolbarTitle: 'Pick your dates' }}
                              slots={{
                                field: MultiInputDateRangeField,
                              }}
                              slotProps={{ field: { dateSeparator: 'to' } }}
                            />
                          </Grid>
                        </Grid>
                      </RadioGroup>
                    </Grid>

                    <Grid sx={classes.spacer} />

                    <Grid container={true} sx={classes.actionButtons}>
                      <Button variant="text" onClick={() => setOpenPauseDialog(false)}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disableElevation={true}
                        loading={isSubmitting}
                        disabled={
                          isSubmitting ||
                          !values.duration ||
                          (values.duration === 'range' && (!values.dateRange.startDate || !values.dateRange.endDate)) ||
                          isRangeGreaterThan4Months(values.dateRange.startDate, values.dateRange.endDate)
                        }
                        sx={classes.pauseButton}
                      >
                        Pause
                      </Button>
                    </Grid>
                  </Form>
                )
              }}
            </Formik>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default PauseDialog
